// Utility functions for safe date handling
export const convertTimestamp = (timestamp) => {
  if (!timestamp) return new Date().toISOString().split('T')[0];
  if (typeof timestamp === 'string') return timestamp;
  
  // Handle Firestore timestamp
  if (timestamp.seconds) {
    const date = new Date(timestamp.seconds * 1000);
    return isNaN(date.getTime()) ? new Date().toISOString().split('T')[0] : date.toISOString().split('T')[0];
  }
  
  // Handle regular date/timestamp
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? new Date().toISOString().split('T')[0] : date.toISOString().split('T')[0];
};

// Safe date to ISO string conversion
export const safeToISOString = (date) => {
  if (!date) return new Date().toISOString();
  const d = new Date(date);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

// Safe date to date string (YYYY-MM-DD)
export const safeToDateString = (date) => {
  if (!date) return new Date().toISOString().split('T')[0];
  const d = new Date(date);
  return isNaN(d.getTime()) ? new Date().toISOString().split('T')[0] : d.toISOString().split('T')[0];
};

// Convert Firestore timestamp to Date object
export const timestampToDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? null : date;
};

// ===== CENTRALIZED DATE FORMATTING SYSTEM =====

// Get company date format with fallback
export const getCompanyDateFormat = (currentCompany) => {
  return currentCompany?.dateFormat ||
         currentCompany?.preferences?.dateFormat ||
         currentCompany?.details?.dateFormat ||
         'DD/MM/YYYY';
};

// Format date according to company settings
export const formatDateToCompanyFormat = (date, currentCompany = null) => {
  if (!date) return '';
  
  let dateObj;
  
  // Handle different date input types
  if (date.seconds) {
    // Firestore timestamp
    dateObj = new Date(date.seconds * 1000);
  } else if (typeof date === 'string' || typeof date === 'number') {
    // String or number timestamp
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    // Already a Date object
    dateObj = date;
  } else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) return '';
  
  const dateFormat = getCompanyDateFormat(currentCompany);
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

// Format date for display (legacy function - now uses company format)
export const formatDate = (date, currentCompany = null) => {
  return formatDateToCompanyFormat(date, currentCompany);
};

// Format date for input fields (always YYYY-MM-DD for HTML date inputs)
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  let dateObj;
  if (date.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) return '';
  
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Parse date from company format to Date object
export const parseDateFromCompanyFormat = (dateString, currentCompany = null) => {
  if (!dateString) return null;
  
  const dateFormat = getCompanyDateFormat(currentCompany);
  let day, month, year;
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      [, month, day, year] = dateString.match(/(\d{2})\/(\d{2})\/(\d{4})/) || [];
      break;
    case 'YYYY-MM-DD':
      [, year, month, day] = dateString.match(/(\d{4})-(\d{2})-(\d{2})/) || [];
      break;
    case 'DD/MM/YYYY':
    default:
      [, day, month, year] = dateString.match(/(\d{2})\/(\d{2})\/(\d{4})/) || [];
      break;
  }
  
  if (!day || !month || !year) return null;
  
  const dateObj = new Date(year, month - 1, day);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

// Get date format examples for display
export const getDateFormatExamples = () => {
  const today = new Date();
  const day = today.getDate().toString().padStart(2, '0');
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear();
  
  return {
    'DD/MM/YYYY': `${day}/${month}/${year}`,
    'MM/DD/YYYY': `${month}/${day}/${year}`,
    'YYYY-MM-DD': `${year}-${month}-${day}`
  };
};
